import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { dailyStandupCostPath, meetingWastePath, rootPath, signInPath } from '@/routes'
import { Link } from '@inertiajs/react'
import { ArrowRight, HelpCircleIcon } from 'lucide-react'

const CAMPAIGN = 'utm_source=standup-bots&utm_campaign=comparison'

const linkClass = 'text-foreground decoration-golden-500 underline underline-offset-4'

type Faq = {
  question: string
  answer: string
}

const APPROACHES = [
  {
    label: 'Who writes the update',
    form: 'Each person, into a bot',
    checkin: 'Each person, in a check-in',
    digest: 'Nobody. The repository does',
  },
  {
    label: 'Source of truth',
    form: 'Whatever someone typed',
    checkin: 'What they wrote, plus items they attach',
    digest: 'Commits and pull requests that landed',
  },
  {
    label: 'Timezones',
    form: 'The bot asks on a local schedule',
    checkin: 'People fill it in when they work',
    digest: 'The digest waits until someone reads it',
  },
  {
    label: 'Work that is not in git',
    form: 'A custom question can cover it',
    checkin: 'A custom question can cover it',
    digest: 'Invisible, unless someone notes it',
  },
  {
    label: 'Common failure',
    form: 'Unread answers and bot fatigue',
    checkin: 'Another form on top of the tools',
    digest: 'A quiet repo looks like a quiet day',
  },
] as const

const PRODUCTS = [
  {
    label: 'Status',
    geekbot: 'On sale',
    standuply: 'On sale',
    range: 'On sale',
    digest: 'On sale',
  },
  {
    label: 'Where it runs',
    geekbot: 'Slack and Microsoft Teams',
    standuply: 'Slack and Microsoft Teams',
    range: 'Range, Slack, and Microsoft Teams',
    digest: 'Slack and email',
  },
  {
    label: 'The update',
    geekbot: 'Answers to bot questions',
    standuply: 'Survey answers, including text, voice, or video',
    range: 'A check-in you write',
    digest: 'A summary of what landed in GitHub',
  },
  {
    label: 'GitHub',
    geekbot: 'Standup questions in Slack or Teams',
    standuply: 'Listed for charts and report data',
    range: 'Suggests issues, pull requests, and reviews to add',
    digest: 'The digest is the GitHub activity',
  },
  {
    label: 'Free',
    geekbot: 'Up to 10 users',
    standuply: '3 automation respondents',
    range: '12 users, up to 2 teams',
    digest: 'See the homepage plan',
  },
  {
    label: 'Paid',
    geekbot: '$3 per participant per month, or $2.50 annually',
    standuply: 'Team from $2 per user per month (1 to 4 users)',
    range: '$8 per team member, month to month',
    digest: '$29 per month, or $25 per month yearly',
  },
] as const

function campaignPath(path: string) {
  return `${path}?${CAMPAIGN}`
}

function TextLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} className={linkClass}>
      {children}
    </Link>
  )
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className={linkClass} target="_blank" rel="noreferrer">
      {children}
    </a>
  )
}

export function StandupBotsArticle({ headline, faqs }: { headline: string; faqs: Faq[] }) {
  return (
    <div className="mx-auto max-w-4xl space-y-12 md:space-y-16">
      <header className="space-y-4 text-center">
        <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">Standup bots</p>
        <h1 className="text-foreground text-4xl font-bold tracking-tight text-balance sm:text-5xl md:text-6xl">
          {headline}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed sm:text-xl">
          Form bots ask people to type what they did. A commit digest summarizes what actually landed in GitHub. Here
          is when each one fits an engineering team.
        </p>
      </header>

      <Card className="border-golden-500/20 from-golden-500/5 via-background to-muted/30 bg-gradient-to-br p-6 text-center sm:p-8">
        <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
          Still holding a live standup next to the bot? Price it on the{' '}
          <TextLink href={campaignPath(dailyStandupCostPath())}>daily standup cost</TextLink> page, or open the{' '}
          <TextLink href={campaignPath(meetingWastePath())}>meeting cost calculator</TextLink>.
        </p>
        <div className="mt-5">
          <Button asChild size="lg" className="bg-golden-500 hover:bg-golden-600 font-semibold text-black">
            <Link href={campaignPath(meetingWastePath())}>
              Open the calculator
              <ArrowRight className="ml-2 size-4 shrink-0" />
            </Link>
          </Button>
        </div>
      </Card>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Code as the source of truth</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Geekbot, Standuply, and Range are all still on sale. Each one collects a status that a person writes.
            Geekbot and Standuply send questions in Slack or Microsoft Teams. Range is a check-in product: you write
            the update, and it can also pull items in from other tools.
          </p>
          <p>
            <TextLink href={rootPath()}>Git Digest</TextLink> takes the other path. It turns GitHub activity into daily
            or weekly team commit digests for Slack and email. The source of truth is what landed, not what someone
            remembered to type. It is not Gitingest. Gitingest turns a repository into text for LLMs. Git Digest is a
            team digest.
          </p>
          <p>
            Prices below are what each company published on its own site on 6 October 2026. Plans change. The links go
            to those pages.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What each product actually does</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            <ExternalLink href="https://geekbot.com/standups/">Geekbot</ExternalLink> asks standup questions in Slack
            or Microsoft Teams. People answer on their own schedule. Geekbot can use each person&apos;s timezone and
            send reminders. The{' '}
            <ExternalLink href="https://geekbot.com/pricing/">pricing page</ExternalLink> lists a free Starter plan
            for up to 10 users. Standups on Basic are $3 per active participant per month, or $2.50 per participant per
            month on the annual plan. Polls and surveys are a separate charge: $1 per respondent per month, or $0.75
            on the annual plan. A new team can start with a 30-day trial.
          </p>
          <p>
            <ExternalLink href="https://standuply.com/">Standuply</ExternalLink> is also a Slack and Microsoft Teams
            bot. It runs standups as surveys, including text, voice, or video answers, plus retrospectives, backlog
            refinement, and planning poker. The homepage says it connects to Jira, GitHub, and other tools to build
            Agile charts. The standup itself is still something people answer. The{' '}
            <ExternalLink href="https://standuply.com/pricing">pricing page</ExternalLink> lists a free Starter plan
            for 3 automation respondents, and a 30-day trial on the homepage. The Team plan is $2 per user per month
            for 1 to 4 users on monthly billing, and $1.50 per user per month for that band when billed annually.
            Larger bands cost more. Business starts at $4 per user per month for 1 to 4 users on monthly billing
            ($3.50 for that band annually). Enterprise is custom. Flat-fee plans are on the same page.
          </p>
          <p>
            <ExternalLink href="https://www.range.co/product/team-check-in-software">Range</ExternalLink> is a
            check-in and meetings product, not only a Slack bot. You share an async check-in, flag blockers, and can
            attach work from other apps. The{' '}
            <ExternalLink href="https://www.range.co/apps/github">GitHub app</ExternalLink> lets you add pull requests
            and issues to a check-in you still write. The{' '}
            <ExternalLink href="https://www.range.co/pricing/">pricing page</ExternalLink> lists a free plan for 12
            users and up to 2 teams, Pro at $8 per team member on month-to-month billing, and a 14-day Pro trial.
            Slack and Microsoft Teams are included. Range also covers meetings, goals, and mood sharing, which a
            commit digest does not.
          </p>
        </div>
      </section>

      <section id="approach-comparison" className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Form bots vs check-ins vs commit digests</h2>
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[44rem] text-left text-sm">
            <caption className="bg-muted/40 text-muted-foreground px-4 py-3 text-left text-xs leading-relaxed">
              Three ways to run an async standup. A commit digest still needs a human note for work that never lands
              in git.
            </caption>
            <thead className="bg-muted/50 text-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  <span className="sr-only">Topic</span>
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Form or status bot
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Check-in app
                </th>
                <th scope="col" className="bg-golden-500/15 px-4 py-3 font-semibold">
                  Commit digest
                </th>
              </tr>
            </thead>
            <tbody>
              {APPROACHES.map((row) => (
                <tr key={row.label} className="border-t">
                  <th scope="row" className="text-foreground px-4 py-3 font-medium">
                    {row.label}
                  </th>
                  <td className="text-muted-foreground px-4 py-3">{row.form}</td>
                  <td className="text-muted-foreground px-4 py-3">{row.checkin}</td>
                  <td className="bg-golden-500/10 text-foreground px-4 py-3">{row.digest}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section id="product-comparison" className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Geekbot, Standuply, Range, and Git Digest</h2>
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[52rem] text-left text-sm">
            <caption className="bg-muted/40 text-muted-foreground px-4 py-3 text-left text-xs leading-relaxed">
              Published product facts as of 6 October 2026. Geekbot standup price is the Basic plan. Standuply Team
              price is the 1 to 4 user monthly band. Git Digest is the homepage plan, for the team rather than per
              person.
            </caption>
            <thead className="bg-muted/50 text-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  <span className="sr-only">Topic</span>
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Geekbot
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Standuply
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Range
                </th>
                <th scope="col" className="bg-golden-500/15 px-4 py-3 font-semibold">
                  Git Digest
                </th>
              </tr>
            </thead>
            <tbody>
              {PRODUCTS.map((row) => (
                <tr key={row.label} className="border-t">
                  <th scope="row" className="text-foreground px-4 py-3 font-medium">
                    {row.label}
                  </th>
                  <td className="text-muted-foreground px-4 py-3">{row.geekbot}</td>
                  <td className="text-muted-foreground px-4 py-3">{row.standuply}</td>
                  <td className="text-muted-foreground px-4 py-3">{row.range}</td>
                  <td className="bg-golden-500/10 text-foreground px-4 py-3">{row.digest}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Geekbot bills standups for active participants, not the whole Slack workspace. Standuply&apos;s per-user
          price rises in larger bands, and it also sells flat-fee plans. Range&apos;s free plan is limited to 12 users
          and 2 teams.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">When to use which</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            <strong className="text-foreground">Use Geekbot or Standuply</strong> when the team will answer a short
            form and the work is not already in git. That includes support, sales, hiring, and operations. Standuply
            is the broader Agile toolkit if you also want retrospectives, planning poker, or voice and video answers.
            Geekbot is the narrower standup and poll bot, with a free tier that covers a small team.
          </p>
          <p>
            <strong className="text-foreground">Use Range</strong> when you want the written check-in plus meetings,
            goals, and mood in one place, and you are willing to keep writing the update. Its GitHub connection helps
            you attach pull requests and issues. It does not remove the writing.
          </p>
          <p>
            <strong className="text-foreground">Use a commit digest</strong> when the engineering team already ships
            in GitHub and the standup has become a retelling of the pull requests. Git Digest posts that summary to
            Slack or email. Keep a one-line note for design, support, and hiring, because a quiet repository is not
            the same as a quiet day.
          </p>
          <p>
            A five-person live standup at $100/hour fully loaded is $30,000 a year before overrun: 5 people × (15 ÷
            60) hours × 5 days a week × 48 working weeks × $100/hour. That is the same formula as the{' '}
            <TextLink href={campaignPath(dailyStandupCostPath())}>daily standup cost</TextLink> page, not a survey.
            Plug in your own salary on the{' '}
            <TextLink href={campaignPath(meetingWastePath())}>meeting cost calculator</TextLink>.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Standup bot FAQ</h2>
          <p className="text-muted-foreground mt-3 text-base">
            Questions about form bots, Range check-ins, and replacing status meetings with GitHub digests.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map((faq) => (
            <Card key={faq.question} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
                  <span className="flex h-[1lh] items-center">
                    <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
                  </span>
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center dark:border-orange-800/30 dark:from-orange-500/10 dark:to-orange-500/5">
        <h2 className="mb-3 text-2xl text-gray-900 dark:text-white">Replace the status form with GitHub digests</h2>
        <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-sm">
          Connect GitHub and get daily or weekly team commit summaries in Slack and email. Keep a form bot if the work
          is not in git. Then{' '}
          <TextLink href={campaignPath(meetingWastePath())}>price any meeting you still keep</TextLink>.
        </p>
        <Button
          asChild
          size="lg"
          className="bg-golden-500 hover:bg-golden-600 font-semibold text-black shadow-lg hover:shadow-xl"
        >
          <Link href={campaignPath(signInPath())}>
            Start with GitHub
            <ArrowRight className="ml-2 size-4 shrink-0" />
          </Link>
        </Button>
      </Card>
    </div>
  )
}
